import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, FileText, Eye, Send, Copy, Trash2, Settings, MoreVertical, Zap, AlertTriangle, Brain, Filter, Calendar, ArrowUpDown, ClipboardList, GraduationCap } from 'lucide-react';
import { formsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { UsageNudgeBanner } from '../components/UsageNudgeBanner';
import { ExpiredPlanBanner } from '../components/ExpiredPlanBanner';

export const DashboardPage = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, formId: null, stats: null, loading: false });
  const [createDialog, setCreateDialog] = useState(false);
  const [createType, setCreateType] = useState('form');
  const [formTitle, setFormTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [upgradeDialog, setUpgradeDialog] = useState({ open: false, message: '' });
  
  // Filter states
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'form', 'quiz'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name', 'submissions'
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { usage, refreshUsage } = useAuth();

  // Import guest draft function
  const importGuestDraft = async () => {
    const savedForm = localStorage.getItem('guest_form_draft');
    if (!savedForm) return;
    
    try {
      const parsed = JSON.parse(savedForm);
      if (!parsed.fields || parsed.fields.length === 0) return;
      
      setCreating(true);
      const response = await formsAPI.create({ 
        title: parsed.title || 'My New Form',
        description: parsed.description || '',
        fields: parsed.fields
      });
      
      // Clear the draft and flag
      localStorage.removeItem('guest_form_draft');
      localStorage.removeItem('create_form_from_draft');
      
      toast.success('Your form draft has been saved!');
      if (refreshUsage) refreshUsage();
      navigate(`/forms/${response.data.id}/builder`);
    } catch (error) {
      console.error('Failed to import draft:', error);
      localStorage.removeItem('create_form_from_draft');
      if (error.response?.status === 402) {
        // Global UpgradeModal handles this via the api.js interceptor.
      } else {
        toast.error('Failed to import your draft. Please try creating a new form.');
      }
    } finally {
      setCreating(false);
    }
  };

  // Check for draft import on mount
  useEffect(() => {
    const importDraft = searchParams.get('import_draft');
    const shouldImport = localStorage.getItem('create_form_from_draft');
    
    if (importDraft === 'true' && shouldImport === 'true') {
      importGuestDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadForms = async () => {
    try {
      const response = await formsAPI.getAll();
      setForms(response.data);
      // Refresh usage stats
      if (refreshUsage) refreshUsage();
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async () => {
    if (!formTitle.trim()) {
      toast.error(`Please enter a ${createType} title`);
      return;
    }

    setCreating(true);
    try {
      const response = await formsAPI.create({ 
        title: formTitle,
        form_type: createType // 'form' or 'quiz'
      });
      toast.success(`${createType === 'quiz' ? 'Quiz' : 'Form'} created successfully!`);
      if (refreshUsage) refreshUsage();
      navigate(`/forms/${response.data.id}/builder`);
    } catch (error) {
      if (error.response?.status === 402) {
        // Global UpgradeModal handles this via the api.js interceptor.
        setCreateDialog(false);
      } else {
        toast.error(`Failed to create ${createType}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (formId) => {
    try {
      await formsAPI.duplicate(formId);
      toast.success('Form duplicated successfully');
      if (refreshUsage) refreshUsage();
      loadForms();
    } catch (error) {
      if (error.response?.status === 402) {
        // Global UpgradeModal handles this via the api.js interceptor.
      } else {
        toast.error('Failed to duplicate form');
      }
    }
  };

  const openDeleteDialog = async (formId) => {
    setDeleteDialog({ open: true, formId, stats: null, loading: true });
    try {
      const res = await formsAPI.getStats(formId);
      setDeleteDialog(prev => ({ ...prev, stats: res.data, loading: false }));
    } catch {
      setDeleteDialog(prev => ({ ...prev, stats: { submissions_count: 0, signed_documents_count: 0 }, loading: false }));
    }
  };

  const handleDelete = async (keepData = false) => {
    try {
      await formsAPI.delete(deleteDialog.formId, keepData);
      toast.success(keepData ? 'Form deleted (submissions & documents preserved)' : 'Form and all data deleted');
      setDeleteDialog({ open: false, formId: null, stats: null, loading: false });
      if (refreshUsage) refreshUsage();
      loadForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const openCreateDialog = () => {
    // Check if user can create form before opening dialog
    if (usage && !usage.can_create_form) {
      setUpgradeDialog({ 
        open: true, 
        message: `You've reached your plan limit of ${usage.limits?.max_forms || 3} forms. Upgrade to Growth for more forms.`
      });
      return;
    }
    // Directly open form creation dialog (skip type selection)
    setCreateType('form');
    setFormTitle('');
    setCreateDialog(true);
  };

  const totalViews = forms.reduce((sum, form) => sum + form.views_count, 0);
  const totalSubmissions = forms.reduce((sum, form) => sum + form.submissions_count, 0);
  const avgConversionRate = totalViews > 0 ? ((totalSubmissions / totalViews) * 100).toFixed(1) : 0;

  // Calculate usage percentages
  const formsUsagePercent = usage ? Math.min((usage.usage?.forms_count / usage.limits?.max_forms) * 100, 100) : 0;
  const submissionsUsagePercent = usage ? Math.min((usage.usage?.submissions_this_month / usage.limits?.max_submissions_per_month) * 100, 100) : 0;

  // Filtered and sorted forms
  const filteredForms = useMemo(() => {
    let result = [...forms];
    
    // Filter by type
    if (typeFilter === 'quiz') {
      result = result.filter(form => form.is_quiz === true);
    } else if (typeFilter === 'form') {
      result = result.filter(form => form.is_quiz !== true);
    }
    
    // Sort
    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'name':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'submissions':
        result.sort((a, b) => b.submissions_count - a.submissions_count);
        break;
      default:
        break;
    }
    
    return result;
  }, [forms, typeFilter, sortBy]);

  // Count forms and quizzes
  const formsCount = forms.filter(f => !f.is_quiz).length;
  const quizzesCount = forms.filter(f => f.is_quiz === true).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-slate-900" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-slate-600 mt-1 text-sm sm:text-base">Manage your forms and track performance</p>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Button variant="outline" asChild size="sm" className="flex-1 sm:flex-none" data-testid="browse-templates-btn">
              <Link to="/templates">Templates</Link>
            </Button>
            <Button onClick={openCreateDialog} size="sm" className="flex items-center space-x-2 flex-1 sm:flex-none" data-testid="create-form-btn">
              <Plus className="w-4 h-4" />
              <span>Create</span>
            </Button>
          </div>
        </div>

        {/* Phase 10: expired-plan read-only banner (priority above usage nudges) */}
        <ExpiredPlanBanner />

        {/* Phase 6: usage nudge when any quota >= 80% */}
        <UsageNudgeBanner />

        {/* Plan Usage Banner */}
        {usage && usage.plan === 'free' && (
          <Card className="bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-200" data-testid="plan-usage-banner">
            <CardContent className="py-4 px-4 sm:px-6">
              <div className="flex flex-col gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded-full uppercase">Free Plan</span>
                    {(formsUsagePercent >= 80 || submissionsUsagePercent >= 80) && (
                      <span className="flex items-center text-amber-600 text-xs sm:text-sm">
                        <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Approaching limit
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <div className="flex justify-between text-xs sm:text-sm mb-1">
                        <span className="text-slate-600">Forms</span>
                        <span className="font-medium">{usage.usage?.forms_count || 0} / {usage.limits?.max_forms || 2}</span>
                      </div>
                      <Progress value={formsUsagePercent} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs sm:text-sm mb-1">
                        <span className="text-slate-600">Submissions</span>
                        <span className="font-medium">{usage.usage?.submissions_this_month || 0} / {usage.limits?.max_submissions_per_month || 50}</span>
                      </div>
                      <Progress value={submissionsUsagePercent} className="h-2" />
                    </div>
                  </div>
                </div>
                <Button asChild size="sm" className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 w-full sm:w-auto sm:self-end">
                  <Link to="/pricing" className="flex items-center justify-center space-x-2">
                    <Zap className="w-4 h-4" />
                    <span>Upgrade</span>
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <Card data-testid="stat-total-forms">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Forms</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{forms.length}</div>
              {usage && usage.plan === 'free' && (
                <p className="text-xs text-muted-foreground mt-1">
                  {usage.forms_remaining} remaining
                </p>
              )}
            </CardContent>
          </Card>
          <Card data-testid="stat-total-views">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalViews}</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-total-submissions">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Submissions</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalSubmissions}</div>
              <p className="text-xs text-muted-foreground mt-1">{avgConversionRate}% conversion</p>
            </CardContent>
          </Card>
        </div>

        {forms.length === 0 ? (
          <Card className="border-dashed" data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">No forms yet</h3>
              <p className="text-slate-600 mb-6 text-center max-w-md">
                Create your first form or quiz from scratch, or choose from our templates
              </p>
              <div className="flex items-center space-x-3">
                <Button onClick={openCreateDialog} data-testid="empty-create-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Create New
                </Button>
                <Button variant="outline" asChild data-testid="empty-templates-btn">
                  <Link to="/templates">Browse Templates</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div>
            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-heading font-semibold text-slate-900">Your Forms</h2>
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {/* Type Filter */}
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="filter-type">
                    <Filter className="w-4 h-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({forms.length})</SelectItem>
                    <SelectItem value="form">
                      <span className="flex items-center">
                        <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
                        Forms ({formsCount})
                      </span>
                    </SelectItem>
                    <SelectItem value="quiz">
                      <span className="flex items-center">
                        <GraduationCap className="w-3.5 h-3.5 mr-1.5" />
                        Quizzes ({quizzesCount})
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Sort By */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[150px] h-9" data-testid="filter-sort">
                    <ArrowUpDown className="w-4 h-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">
                      <span className="flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1.5" />
                        Newest First
                      </span>
                    </SelectItem>
                    <SelectItem value="oldest">
                      <span className="flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1.5" />
                        Oldest First
                      </span>
                    </SelectItem>
                    <SelectItem value="name">A-Z by Name</SelectItem>
                    <SelectItem value="submissions">Most Submissions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Filtered Results Info */}
            {typeFilter !== 'all' && (
              <div className="text-sm text-slate-500 mb-4">
                Showing {filteredForms.length} {typeFilter === 'quiz' ? 'quiz' : 'form'}{filteredForms.length !== 1 ? 'zes' : ''}
                {typeFilter === 'form' && filteredForms.length !== 1 && 's'}
                <button 
                  onClick={() => setTypeFilter('all')} 
                  className="ml-2 text-indigo-600 hover:text-indigo-700 underline"
                >
                  Show all
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredForms.map((form) => (
                <Card key={form.id} className="hover:shadow-lg transition-shadow duration-200" data-testid={`form-card-${form.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-lg line-clamp-1">{form.title}</CardTitle>
                          {/* Form/Quiz Badge */}
                          {form.is_quiz ? (
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-xs px-2 py-0.5 flex items-center gap-1" data-testid="quiz-badge">
                              <GraduationCap className="w-3 h-3" />
                              Quiz
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs px-2 py-0.5 flex items-center gap-1" data-testid="form-badge">
                              <ClipboardList className="w-3 h-3" />
                              Form
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="line-clamp-2 mt-1">
                          {form.description || 'No description'}
                        </CardDescription>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="-mt-2 -mr-2" data-testid={`form-menu-${form.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/forms/${form.id}/builder`)} data-testid="menu-edit">
                            <Settings className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(form.id)} data-testid="menu-duplicate">
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openDeleteDialog(form.id)}
                            className="text-red-600"
                            data-testid="menu-delete"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{form.views_count}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Send className="w-4 h-4" />
                          <span>{form.submissions_count}</span>
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${form.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button size="sm" className="flex-1" onClick={() => navigate(`/forms/${form.id}/builder`)} data-testid="view-form-btn">
                        Open {form.is_quiz ? 'Quiz' : 'Form'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/forms/${form.id}/submissions`)} data-testid="view-submissions-btn">
                        Submissions
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent data-testid="create-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              Create New Form
            </DialogTitle>
            <DialogDescription>Enter a title for your new form</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">{createType === 'quiz' ? 'Quiz' : 'Form'} Title</Label>
              <Input
                id="title"
                placeholder={createType === 'quiz' ? 'My Awesome Quiz' : 'My Awesome Form'}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateForm()}
                data-testid="form-title-input"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setCreateDialog(false)} data-testid="cancel-create-btn">
              Cancel
            </Button>
            <Button onClick={handleCreateForm} disabled={creating} data-testid="confirm-create-btn">
              {creating ? 'Creating...' : `Create ${createType === 'quiz' ? 'Quiz' : 'Form'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, formId: null, stats: null, loading: false })}>
        <AlertDialogContent data-testid="delete-form-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>How would you like to delete this form?</p>
                {deleteDialog.loading ? (
                  <p className="text-sm text-slate-400">Loading stats...</p>
                ) : deleteDialog.stats && (deleteDialog.stats.submissions_count > 0 || deleteDialog.stats.signed_documents_count > 0) ? (
                  <div className="flex gap-4 text-sm">
                    <span className="text-slate-600"><strong>{deleteDialog.stats.submissions_count}</strong> submissions</span>
                    <span className="text-slate-600"><strong>{deleteDialog.stats.signed_documents_count}</strong> signed documents</span>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4 border-amber-200 hover:bg-amber-50 text-left"
              onClick={() => handleDelete(true)}
              data-testid="delete-keep-data-btn"
            >
              <div>
                <p className="font-medium text-slate-800">Delete form only</p>
                <p className="text-xs text-slate-500 font-normal">Keep submissions & signed PDFs for future verification</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4 border-red-200 hover:bg-red-50 text-left"
              onClick={() => handleDelete(false)}
              data-testid="confirm-delete-btn"
            >
              <div>
                <p className="font-medium text-red-600">Delete everything</p>
                <p className="text-xs text-slate-500 font-normal">Permanently remove form, submissions & signed documents</p>
              </div>
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialog.open} onOpenChange={(open) => setUpgradeDialog({ open, message: '' })}>
        <DialogContent data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-amber-500" />
              <span>Upgrade Required</span>
            </DialogTitle>
            <DialogDescription className="pt-2">
              {upgradeDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg p-4 mt-2">
            <h4 className="font-semibold text-slate-900 mb-2">Growth Plan - ₹699/month</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• 25 forms</li>
              <li>• 2,500 submissions/month</li>
              <li>• Up to 200 fields per form</li>
              <li>• Custom header branding</li>
              <li>• All integrations</li>
            </ul>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setUpgradeDialog({ open: false, message: '' })}>
              Maybe Later
            </Button>
            <Button asChild className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700">
              <Link to="/pricing">
                <Zap className="w-4 h-4 mr-2" />
                View Plans
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};